import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Header from "../components/store/Header";
import Hero from "../components/store/Hero";
import Marquee from "../components/store/Marquee";
import Catalog from "../components/store/Catalog";
import Atmosphere from "../components/store/Atmosphere";
import Services from "../components/store/Services";
import Simulators from "../components/store/Simulators";
import ReviewsSection from "../components/store/ReviewsSection";
import ContactSection from "../components/store/ContactSection";
import Footer from "../components/store/Footer";
import CartDrawer from "../components/store/CartDrawer";

const DEFAULT_SECTIONS = [
  { key: "hero", visible: true },
  { key: "marquee", visible: true },
  { key: "catalog", visible: true },
  { key: "atmosphere", visible: true },
  { key: "services", visible: true },
  { key: "simulators", visible: true },
  { key: "reviews", visible: true },
];

export default function StorePage() {
  const [settings, setSettings] = useState({ store_name: "Dra.Cell Assistência Técnica", whatsapp_number: "", address: "", logo_url: "" });
  const [content, setContent] = useState(null);
  const [products, setProducts] = useState([]);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    api.get("/settings").then((res) => setSettings(res.data)).catch(() => {});
    api.get("/products").then((res) => setProducts(res.data)).catch(() => {});
    // modo pré-visualização: lê o rascunho salvo pelo painel (sem publicar)
    if (new URLSearchParams(window.location.search).get("preview") === "1") {
      const draft = localStorage.getItem("draft_content");
      if (draft) {
        try {
          setContent(JSON.parse(draft));
          setIsPreview(true);
          return;
        } catch {}
      }
    }
    api.get("/content").then((res) => setContent(res.data)).catch(() => {});
  }, []);

  // Tema editável: cor de destaque + fontes + cor de fundo
  useEffect(() => {
    const theme = content?.theme || {};
    if (theme.bg) document.documentElement.style.setProperty("--site-bg", theme.bg);
    if (theme.accent) {
      document.documentElement.style.setProperty("--brand", theme.accent);
      const hex = theme.accent.replace("#", "");
      const darken = (f) => `#${[0, 2, 4].map((i) => Math.max(0, parseInt(hex.slice(i, i + 2), 16) * (1 - f)).toString(16).padStart(2, "0")).join("")}`;
      document.documentElement.style.setProperty("--brand-dark", darken(0.18));
    }
    const FONTS = {
      padrao: null,
      elegante: { display: "'Playfair Display', Georgia, serif", body: "'Inter', Arial, sans-serif", url: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600&display=swap" },
      moderna: { display: "'Sora', Arial, sans-serif", body: "'Work Sans', Arial, sans-serif", url: "https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Work+Sans:wght@400;500&display=swap" },
      classica: { display: "'Merriweather', Georgia, serif", body: "'Karla', Arial, sans-serif", url: "https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&family=Karla:wght@400;500;600&display=swap" },
    };
    const font = FONTS[theme.font];
    if (font) {
      let link = document.getElementById("theme-fonts");
      if (!link) {
        link = document.createElement("link");
        link.id = "theme-fonts";
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
      link.href = font.url;
      document.documentElement.style.setProperty("--font-display", font.display);
      document.documentElement.style.setProperty("--font-body", font.body);
    }
  }, [content]);

  // SEO editável pelo painel
  useEffect(() => {
    if (content?.seo?.title) document.title = content.seo.title;
    if (content?.seo?.description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = content.seo.description;
    }
    if (settings.logo_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (link) link.href = `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`;
    }
  }, [content, settings.logo_url]);

  const sections = content?.sections?.length ? content.sections : DEFAULT_SECTIONS;

  const renderSection = (key) => {
    switch (key) {
      case "hero":
        return <Hero whatsappNumber={settings.whatsapp_number} content={content} />;
      case "marquee":
        return <Marquee items={content?.marquee} />;
      case "catalog":
        return <Catalog />;
      case "atmosphere":
        return <Atmosphere whatsappNumber={settings.whatsapp_number} content={content} />;
      case "services":
        return <Services whatsappNumber={settings.whatsapp_number} address={settings.address} hours={content?.hours} services={content?.services} />;
      case "simulators":
        return <Simulators whatsappNumber={settings.whatsapp_number} />;
      case "reviews":
        return <ReviewsSection products={products} />;
      case "contact":
        return <ContactSection whatsappNumber={settings.whatsapp_number} />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-[var(--site-bg)] text-[#1A1816] min-h-screen antialiased overflow-x-hidden">
      {content?.announcement?.active && content?.announcement?.text && (
        <div className="bg-[var(--brand)] text-white text-center text-xs font-display font-semibold tracking-wide py-2.5 px-4" data-testid="announcement-bar">
          {content.announcement.text}
        </div>
      )}
      {isPreview && (
        <div className="bg-yellow-400 text-[#1A1816] text-center text-xs font-display font-semibold tracking-wide py-2.5 px-4" data-testid="preview-banner">
          MODO PRÉ-VISUALIZAÇÃO — estas alterações ainda não foram publicadas
        </div>
      )}
      <Header storeName={settings.store_name} logoUrl={settings.logo_url} />
      {sections.filter((s) => s.visible !== false).map((s) => (
        <div key={s.key}>{renderSection(s.key)}</div>
      ))}
      <Footer
        storeName={settings.store_name}
        whatsappNumber={settings.whatsapp_number}
        logoUrl={settings.logo_url}
        hours={content?.hours}
        instagram={content?.instagram}
      />
      <CartDrawer whatsappNumber={settings.whatsapp_number} />
    </div>
  );
}
